/**
 * resource-picker-dialog.tsx — Chọn phạm vi nguồn tri thức cho bot (guardrail).
 *
 * Dùng chung cho thư mục tài liệu bán hàng (DocFolder, có cây) và nhóm kiến
 * thức (KnowledgeCategory, có thể có cha). Hiện dạng cây thụt lề; tick thư
 * mục cha là gồm cả con (BE mở rộng thư mục con), nên con hiện "đã gồm" và
 * không tick được riêng. Không chọn gì = toàn bộ.
 */
import { useMemo, useState } from 'react'
import { Database, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'

export interface ResourceItem {
  id: string
  name: string
  parentId?: string | null
  /** Số bài / tài liệu — hiện mờ bên phải nếu có. */
  count?: number
}

/** Sắp cây → danh sách phẳng kèm độ sâu (cha trước con). */
export function xepCay(items: ResourceItem[]): Array<ResourceItem & { depth: number; toTien: string[] }> {
  const conCua = new Map<string | null, ResourceItem[]>()
  const coId = new Set(items.map((i) => i.id))
  for (const it of items) {
    // Cha không nằm trong danh sách (bị lọc) → coi như gốc.
    const cha = it.parentId && coId.has(it.parentId) ? it.parentId : null
    if (!conCua.has(cha)) conCua.set(cha, [])
    conCua.get(cha)!.push(it)
  }
  const out: Array<ResourceItem & { depth: number; toTien: string[] }> = []
  const di = (cha: string | null, depth: number, toTien: string[]) => {
    for (const it of conCua.get(cha) ?? []) {
      out.push({ ...it, depth, toTien })
      di(it.id, depth + 1, [...toTien, it.id])
    }
  }
  di(null, 0, [])
  return out
}

export function ResourcePickerDialog({ title, moTa, items, selected, canEdit, onClose, onSave, saving }: {
  title: string
  moTa?: string
  items: ResourceItem[]
  selected: string[]
  canEdit: boolean
  onClose: () => void
  onSave: (ids: string[]) => void
  saving?: boolean
}) {
  const [ids, setIds] = useState<string[]>(selected)
  const cay = useMemo(() => xepCay(items), [items])
  const toggle = (id: string) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {moTa ?? 'Tick phần bot được phép tra cứu. Không tick gì = toàn bộ. Giới hạn được chặn trong code — bot không vượt được.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-80 space-y-0.5 overflow-y-auto rounded-lg border p-2">
          {cay.length === 0 ? (
            <p className="p-2 text-xs text-muted-foreground">Chưa có mục nào.</p>
          ) : cay.map((c) => {
            const chaDaTick = c.toTien.some((t) => ids.includes(t))
            const checked = ids.includes(c.id) || chaDaTick
            return (
              <label
                key={c.id}
                className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted ${chaDaTick ? 'opacity-60' : ''}`}
                style={{ paddingLeft: 8 + c.depth * 18 }}
                title={chaDaTick ? 'Đã gồm trong thư mục cha' : undefined}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} disabled={!canEdit || chaDaTick} />
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                {chaDaTick && <span className="text-[10px] text-muted-foreground">đã gồm</span>}
                {typeof c.count === 'number' && !chaDaTick && (
                  <span className="text-[10px] tabular-nums text-muted-foreground">{c.count}</span>
                )}
              </label>
            )
          })}
        </div>
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setIds([])} disabled={!canEdit || ids.length === 0}>Bỏ giới hạn (toàn bộ)</Button>
          <Button onClick={() => onSave(ids)} disabled={!canEdit || saving}>
            <Save className="h-4 w-4" /> {saving ? 'Đang lưu…' : ids.length === 0 ? 'Lưu: toàn bộ' : `Lưu ${ids.length} mục`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
